import { APP_VERSION, ENV, isLocalDev } from 'env';

type LogTypes = 'error' | 'info' | 'warn' | 'debug';
type DatadogLogsApi = (typeof import('@datadog/browser-logs'))['datadogLogs'];
type DatadogRumApi = (typeof import('@datadog/browser-rum'))['datadogRum'];

export const isRemoteLoggingEnabled = !isLocalDev;

let datadogLogs: Promise<DatadogLogsApi> | undefined;
let datadogRum: Promise<DatadogRumApi> | undefined;
let datadogLogsInitialized: Promise<DatadogLogsApi> | undefined;
let datadogRumInitialized: Promise<DatadogRumApi> | undefined;

const buildConfiguration = () => ({
    clientToken: 'pub67a942f6d9751ed19ecb8e3d6ac580a0',
    site: 'datadoghq.com',
    service: 'command-center',
    env: ENV,
    version: APP_VERSION,
    sampleRate: 100,
});

const ignoreDatadogError = () => undefined;

const loadDatadogLogs = () => {
    datadogLogs ??= import('@datadog/browser-logs').then((module) => module.datadogLogs);
    return datadogLogs;
};

const loadDatadogRum = () => {
    datadogRum ??= import('@datadog/browser-rum').then((module) => module.datadogRum);
    return datadogRum;
};

const initializeDatadogLogs = () => {
    const configuration = buildConfiguration();

    datadogLogsInitialized ??= loadDatadogLogs().then((logs) => {
        logs.init({ ...configuration, forwardErrorsToLogs: true });
        return logs;
    });

    return datadogLogsInitialized;
};

const initializeDatadogRum = () => {
    const configuration = buildConfiguration();

    datadogRumInitialized ??= loadDatadogRum().then((rum) => {
        rum.init({
            ...configuration,
            applicationId: '0aa0df5b-bd64-4b81-bea9-7e06ba940222',
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'mask',
            allowedTracingUrls:
                ENV === 'prd'
                    ? ['https://command.ndvr.com', 'https://hub.claroadvisors.com']
                    : [/https:\/\/.+\.internal\.ndvr\.com/],
        });
        return rum;
    });

    return datadogRumInitialized;
};

export const initializeRemoteLogging = () => {
    if (!isRemoteLoggingEnabled) {
        // don't do any DataDog logging for local environments
        return;
    }

    void initializeDatadogLogs().catch(ignoreDatadogError);
    void initializeDatadogRum().catch(ignoreDatadogError);
};

export const addRumError = (...args: Parameters<DatadogRumApi['addError']>) => {
    if (!isRemoteLoggingEnabled) {
        return;
    }

    void initializeDatadogRum()
        .then((rum) => rum.addError(...args))
        .catch(ignoreDatadogError);
};

export const setRumUser = (...args: Parameters<DatadogRumApi['setUser']>) => {
    if (!isRemoteLoggingEnabled) {
        return;
    }

    void initializeDatadogRum()
        .then((rum) => rum.setUser(...args))
        .catch(ignoreDatadogError);
};

export const clearRumUser = () => {
    if (!isRemoteLoggingEnabled) {
        return;
    }

    void initializeDatadogRum()
        .then((rum) => rum.clearUser())
        .catch(ignoreDatadogError);
};

export const logToDatadog = (type: LogTypes, message: any, ...rest: any[]) => {
    if (!isRemoteLoggingEnabled) {
        return;
    }

    void initializeDatadogLogs()
        .then((logs) => logs.logger[type](message, ...rest))
        .catch(ignoreDatadogError);
};
